import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { listReports, resolveReport, listRecentActions } from "@/lib/moderation-community.functions";

export const Route = createFileRoute("/_authenticated/admin/moderation/community")({
  component: CommunityModerationPage,
  errorComponent: ({ error }) => <div className="p-6 text-destructive">{error.message}</div>,
  notFoundComponent: () => <div className="p-6">Not found</div>,
});

function CommunityModerationPage() {
  const router = useRouter();
  const list = useServerFn(listReports);
  const actions = useServerFn(listRecentActions);
  const resolve = useServerFn(resolveReport);
  const [status, setStatus] = useState<"open" | "actioned" | "dismissed">("open");

  const reportsQ = useQuery({
    queryKey: ["community-reports", status],
    queryFn: () => list({ data: { status } as any }),
  });
  const actionsQ = useQuery({ queryKey: ["community-mod-actions"], queryFn: () => actions() });

  async function decide(id: string, resolution: "hide" | "unhide" | "dismiss" | "warn" | "suspend", suspendDays?: number) {
    try {
      await resolve({ data: { id, resolution, suspendDays } });
      toast.success(`Report ${resolution}d`);
      router.invalidate();
      reportsQ.refetch();
      actionsQ.refetch();
    } catch (e: any) {
      toast.error(e?.message ?? "Failed");
    }
  }

  return (
    <div className="container max-w-5xl py-6 space-y-6">
      <div>
        <h1 className="text-2xl font-serif">Community Moderation</h1>
        <p className="text-sm text-muted-foreground">Reports, hides, and strikes across the temple.</p>
      </div>

      <Tabs value={status} onValueChange={(v) => setStatus(v as any)}>
        <TabsList>
          <TabsTrigger value="open">Open</TabsTrigger>
          <TabsTrigger value="actioned">Actioned</TabsTrigger>
          <TabsTrigger value="dismissed">Dismissed</TabsTrigger>
        </TabsList>

        <TabsContent value={status} className="space-y-3">
          {reportsQ.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
          {reportsQ.data?.length === 0 && <p className="text-sm text-muted-foreground">Nothing here.</p>}
          {(reportsQ.data ?? []).map((r: any) => (
            <Card key={r.id} className="p-4 space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline">{r.target_kind}</Badge>
                <Badge>{r.reason}</Badge>
                <span className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString()}</span>
              </div>
              <div className="text-xs text-muted-foreground font-mono break-all">
                target: {r.target_id}
              </div>
              {r.notes && <p className="text-sm">{r.notes}</p>}
              {status === "open" && (
                <div className="flex flex-wrap gap-2 pt-2">
                  <Button size="sm" variant="destructive" onClick={() => decide(r.id, "hide")}>Hide</Button>
                  <Button size="sm" variant="outline" onClick={() => decide(r.id, "unhide")}>Unhide</Button>
                  <Button size="sm" variant="secondary" onClick={() => decide(r.id, "warn")}>Warn</Button>
                  <Button size="sm" variant="destructive" onClick={() => decide(r.id, "suspend", 7)}>Suspend 7d</Button>
                  <Button size="sm" variant="ghost" onClick={() => decide(r.id, "dismiss")}>Dismiss</Button>
                </div>
              )}
            </Card>
          ))}
        </TabsContent>
      </Tabs>

      <div>
        <h2 className="text-lg font-serif mb-3">Recent moderator actions</h2>
        <div className="space-y-2">
          {(actionsQ.data ?? []).map((a: any) => (
            <div key={a.id} className="text-sm flex items-center gap-2 flex-wrap border-b border-border/40 pb-2">
              <Badge variant="outline">{a.action}</Badge>
              <span className="text-muted-foreground">{a.target_kind}</span>
              <span className="text-xs font-mono">{a.target_id.slice(0, 8)}…</span>
              {a.reason && <span className="text-xs text-muted-foreground">— {a.reason}</span>}
              <span className="ml-auto text-xs text-muted-foreground">{new Date(a.created_at).toLocaleString()}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
